import { BuscaPaciente, type PacienteResumo } from './BuscaPaciente.tsx';
import { Campo, Selecao } from './forma.tsx';
import type { Opcoes } from '../lib/cadastro.ts';
import { MODOS_ENVIO, paraCampoDinheiro, TIPOS_EXAME } from '../lib/formato.ts';

/* Os campos do exame, em um lugar só.
 *
 * Eles aparecem em duas telas: no editor do exame e no atendimento, que cadastra
 * paciente e exame sem trocar de tela. Duplicar o formulário faria as duas
 * divergirem na primeira correção feita de um lado só -- e a diferença
 * apareceria como exame gravado sem um campo que a outra tela exige.
 *
 * O que muda entre as duas é o entorno, não os campos: o editor mostra situação
 * e trava tudo quando há laudo assinado; o atendimento é sempre exame novo. */

export interface FormExame {
  dataExame: string;
  pacienteId: string;
  procedimentoId: string;
  tipo: string;
  situacao: string;
  modoEnvio: string;
  empresaId: string;
  convenioId: string;
  medicoId: string;
  funcao: string;
  /** Marcado no exame: e o que decide se ele entra na lista ocupacional. */
  medicinaOcupacional: string;
  /** Quem pediu, escrito a mao -- pode nao existir no sistema. */
  medicoSolicitante: string;
  medicoSolicitanteCrm: string;
  /** Quem acompanha pelo proprio acesso, escolhido da lista de medicos. */
  medicoAcompanhamentoId: string;
  valorExame: string;
}

export function exameVazio(dataExame: string): FormExame {
  return {
    dataExame,
    pacienteId: '',
    procedimentoId: '',
    tipo: 'SOLICITACAO_MEDICA',
    situacao: 'AGUARDANDO_EXAME',
    modoEnvio: 'LAUDO_NO_SISTEMA',
    empresaId: '',
    convenioId: '',
    medicoId: '',
    funcao: '',
    medicinaOcupacional: '',
    medicoSolicitante: '',
    medicoSolicitanteCrm: '',
    medicoAcompanhamentoId: '',
    valorExame: '',
  };
}

/** Preenche valor do exame e do laudo com a tabela do procedimento, sem
 *  sobrescrever o que já foi digitado à mão. */
export function aoTrocarProcedimento(
  f: FormExame,
  procedimentoId: string,
  opcoes: Opcoes | undefined,
): FormExame {
  const escolhido = opcoes?.procedimentos.find((p) => String(p.id) === procedimentoId);
  return {
    ...f,
    procedimentoId,
    /* A tabela do procedimento escolhido MANDA no valor; o que estava no campo
       so fica quando o procedimento nao tem preco cadastrado.

       Era o contrario -- o campo preenchido vencia a tabela --, e isso e
       aceitavel enquanto o formulario nasce vazio. Deixou de ser quando o
       atendimento passou a guardar os campos de um exame para o proximo: o
       cotovelo herdaria o preco do torax, e a diferenca so apareceria no
       faturamento, semanas depois, sem nada na tela para desconfiar.

       Preco digitado a mao continua valendo enquanto o procedimento for o
       mesmo. Trocar de procedimento e trocar de exame, e o preco vai junto. */
    valorExame: paraCampoDinheiro(escolhido?.valor) || f.valorExame,
    /* O procedimento SUGERE a marcacao ocupacional; nao decide. Um "TORAX OIT"
       so existe para medicina do trabalho, e repetir isso a mao a cada exame
       seria trabalho a toa -- mas quem cadastra pode desmarcar, e um exame
       comum pedido pela empresa pode ser marcado. So preenche o que esta em
       branco: escolha ja feita nao se desfaz ao trocar de procedimento. */
    medicinaOcupacional:
      f.medicinaOcupacional || (escolhido?.medicinaOcupacional ? 'true' : ''),
  };
}

export function CamposDoExame({
  form,
  campos,
  opcoes,
  paciente,
  aoMudar,
  aoEscolherPaciente,
  bloqueado = false,
  mostrarPaciente = true,
  mostrarSituacao = false,
  mostrarValores = false,
}: {
  form: FormExame;
  campos: Record<string, string>;
  opcoes: Opcoes | undefined;
  paciente: PacienteResumo | null;
  aoMudar: (mudanca: Partial<FormExame>) => void;
  aoEscolherPaciente: (p: PacienteResumo | null) => void;
  /** Laudo assinado congela o cadastro. */
  bloqueado?: boolean;
  /** No atendimento o paciente já foi escolhido no passo anterior. */
  mostrarPaciente?: boolean;
  /** Só faz sentido em exame que já existe. */
  mostrarSituacao?: boolean;
  mostrarValores?: boolean;
}) {
  const procedimento = opcoes?.procedimentos.find((p) => String(p.id) === form.procedimentoId);

  const mudar =
    <C extends keyof FormExame>(chave: C) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      aoMudar({ [chave]: e.target.value } as Partial<FormExame>);

  return (
    <>
      {mostrarPaciente && (
        <BuscaPaciente
          valor={paciente}
          aoEscolher={aoEscolherPaciente}
          erro={campos.pacienteId}
          desabilitado={bloqueado}
        />
      )}

      <Campo
        rotulo="Data do exame"
        type="date"
        largura="meia"
        obrigatorio
        value={form.dataExame}
        erro={campos.dataExame}
        onChange={mudar('dataExame')}
        disabled={bloqueado}
      />
      <Selecao
        rotulo="Tipo"
        largura="meia"
        obrigatorio
        value={form.tipo}
        erro={campos.tipo}
        onChange={mudar('tipo')}
        disabled={bloqueado}
      >
        {Object.entries(TIPOS_EXAME).map(([v, r]) => (
          <option key={v} value={v}>
            {r}
          </option>
        ))}
      </Selecao>

      <Selecao
        rotulo="Exame"
        obrigatorio
        /* O atendimento devolve o foco para cá depois de gravar um exame: é o
           único campo que ele limpa, e por isso o único que resta preencher. */
        name="procedimentoId"
        value={form.procedimentoId}
        erro={campos.procedimentoId}
        onChange={(e) => aoMudar(aoTrocarProcedimento(form, e.target.value, opcoes))}
        disabled={bloqueado}
        dica={
          procedimento
            ? procedimento.tipoLaudo === 'OIT_2000'
              ? 'O médico vai preencher a folha OIT 2000.'
              : 'O médico vai preencher o laudo em texto.'
            : undefined
        }
      >
        <option value="">Selecione</option>
        {opcoes?.procedimentos.map((p) => (
          <option key={p.id} value={p.id}>
            {p.nome}
          </option>
        ))}
      </Selecao>

      <Selecao
        rotulo="Empresa"
        largura="meia"
        value={form.empresaId}
        erro={campos.empresaId}
        onChange={mudar('empresaId')}
        disabled={bloqueado}
        dica="Para exames de medicina do trabalho."
      >
        <option value="">Nenhuma</option>
        {opcoes?.empresas.map((e) => (
          <option key={e.id} value={e.id}>
            {e.nome}
          </option>
        ))}
      </Selecao>
      <Selecao
        rotulo="Convênio"
        largura="meia"
        value={form.convenioId}
        erro={campos.convenioId}
        onChange={mudar('convenioId')}
        disabled={bloqueado}
      >
        <option value="">Nenhum</option>
        {opcoes?.convenios.map((c) => (
          <option key={c.id} value={c.id}>
            {c.nome}
          </option>
        ))}
      </Selecao>

      <Campo
        rotulo="Função na empresa"
        largura="meia"
        value={form.funcao}
        erro={campos.funcao}
        onChange={mudar('funcao')}
        disabled={bloqueado}
      />

      {/* Obrigatório, e sem opção pré-marcada: é o que decide se o exame
          aparece na lista de retirada da clínica ocupacional. Deixar um padrão
          faria a resposta vir por omissão, e exame ocupacional esquecido nunca
          é buscado por ninguém. */}
      <Selecao
        rotulo="Medicina do trabalho"
        largura="meia"
        obrigatorio
        value={form.medicinaOcupacional}
        erro={campos.medicinaOcupacional}
        onChange={mudar('medicinaOcupacional')}
        disabled={bloqueado}
        dica={
          form.medicinaOcupacional === 'true'
            ? 'Vai aparecer na lista de retirada da clínica ocupacional.'
            : 'Foi pedido pela medicina do trabalho de alguma empresa?'
        }
      >
        <option value="">Selecione</option>
        <option value="true">Sim</option>
        <option value="false">Não</option>
      </Selecao>

      {/* Quem PEDIU o exame: vem escrito no pedido de papel, e na maioria das
          vezes não é médico da clínica nem existe no sistema. Por isso é texto
          livre -- exigir que estivesse cadastrado obrigaria a recepção a criar
          um médico só para registrar um nome. */}
      <Campo
        rotulo="Médico solicitante"
        largura="meia"
        value={form.medicoSolicitante}
        erro={campos.medicoSolicitante}
        onChange={mudar('medicoSolicitante')}
        disabled={bloqueado}
        dica="Como veio no pedido."
      />
      <Campo
        rotulo="CRM do solicitante"
        largura="meia"
        value={form.medicoSolicitanteCrm}
        erro={campos.medicoSolicitanteCrm}
        onChange={mudar('medicoSolicitanteCrm')}
        disabled={bloqueado}
      />

      {/* Outra coisa: quem vai ACOMPANHAR o exame pelo sistema. Escolhido da
          lista porque é o vínculo que dá a ele a tela "exames que solicitei" --
          um nome digitado não abriria acesso a ninguém. */}
      <Selecao
        rotulo="Acompanhamento médico"
        largura="meia"
        value={form.medicoAcompanhamentoId}
        erro={campos.medicoAcompanhamentoId}
        onChange={mudar('medicoAcompanhamentoId')}
        disabled={bloqueado}
        dica="Ele acompanha este exame pelo próprio acesso e retira os prontos."
      >
        <option value="">Nenhum</option>
        {opcoes?.solicitantes.map((m) => (
          <option key={m.id} value={m.id}>
            {m.nome} — {m.crm}
          </option>
        ))}
      </Selecao>
      <Selecao
        rotulo="Médico que vai laudar"
        largura="meia"
        value={form.medicoId}
        erro={campos.medicoId}
        onChange={mudar('medicoId')}
        disabled={bloqueado}
        dica="Pode ficar em branco e ser definido depois."
      >
        <option value="">A definir</option>
        {opcoes?.medicos.map((m) => (
          <option key={m.id} value={m.id}>
            {m.nome} — {m.crm}
          </option>
        ))}
      </Selecao>

      <Selecao
        rotulo="Como o laudo será feito"
        value={form.modoEnvio}
        erro={campos.modoEnvio}
        onChange={mudar('modoEnvio')}
        disabled={bloqueado}
        dica={
          form.modoEnvio === 'SEM_LAUDO'
            ? 'O exame não entra na fila do médico: quando a imagem chegar, ele já fica finalizado. Se depois o paciente quiser o laudo, é só mandar para o médico pela fila.'
            : form.modoEnvio === 'PORTAL'
              ? 'O laudo sai por um portal de fora. O exame já fica finalizado ao salvar, não entra na fila de nenhum médico, e o documento com as imagens fica disponível para baixar.'
              : undefined
        }
      >
        {Object.entries(MODOS_ENVIO).map(([v, r]) => (
          <option key={v} value={v}>
            {r}
          </option>
        ))}
      </Selecao>

      {mostrarSituacao && (
        <Selecao
          rotulo="Situação"
          largura="meia"
          value={form.situacao}
          erro={campos.situacao}
          onChange={mudar('situacao')}
          disabled={bloqueado}
        >
          <option value="AGUARDANDO_EXAME">Aguardando exame</option>
          <option value="AGUARDANDO_LAUDO">Aguardando laudo</option>
          {/* Recusado não se escolhe à mão -- quem recusa é o médico, com
              motivo. Está aqui só para o campo não mentir sobre o estágio
              atual de um exame que voltou. */}
          <option value="RECUSADO" disabled>
            Recusado
          </option>
          <option value="FINALIZADO">Finalizado</option>
          <option value="CANCELADO">Cancelado</option>
        </Selecao>
      )}

      {mostrarValores && (
        <>
          <Campo
            rotulo="Valor do exame"
            largura="meia"
            inputMode="numeric"
            placeholder="0,00"
            value={form.valorExame}
            erro={campos.valorExame}
            onChange={mudar('valorExame')}
            disabled={bloqueado}
          />
        </>
      )}
    </>
  );
}
